import styles from './CheckoutBasePage.scss';

import cx from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, useLocation } from 'react-router-dom';

import { LinkVariant } from 'src/api/contentful/contentTypes.cloverdotcom';
import { useCart } from 'src/components/Cart/UseCart';
import { CheckoutRoutePath } from 'src/components/Checkout/Checkout';
import { Grid } from 'src/components/Grid/Grid';
import { GridContainer } from 'src/components/GridContainer/GridContainer';
import { OrderSummary } from 'src/components/OrderSummary/OrderSummary';
import { ResponsiveProgressBar } from 'src/components/ResponsiveProgressBar/ResponsiveProgressBar';
import { StructuredModal } from 'src/components/StructuredModal/StructuredModal';
import { redesignV2RoutePath } from 'src/constants/redesignV2';

import { useDesktopLarge } from 'src/hooks/UseResponsive';
import { CheckoutLoading } from '../CheckoutLoading/CheckoutLoading';
import { PROGRESS_BAR_STEPS, PROGRESS_BAR_STEP_ENUM } from '../CheckoutProgressBar.config';
import { useCheckout } from '../CheckoutProvider';

export interface CheckoutBasePageProps {
  children: React.ReactNode;
}

export const CheckoutBasePage: React.FC<CheckoutBasePageProps> = ({
  children,
}) => {
  const location = useLocation();
  const { cart } = useCart();

  const { t } = useTranslation();
  const { isUserInfoValid, continueButtonRef, userInfo, isLoading, hasApiError, setHasApiError } = useCheckout();
  const isLargeDesktop = useDesktopLarge();

  const isCartEmpty = !cart.bundle && (!cart.products || !cart.products.length);
  if (isCartEmpty) {
    return <Redirect to={redesignV2RoutePath.CART} />;
  }

  const continueButtonClickHandler = () => {
    continueButtonRef.current?.click();
  };

  const infoPathname = redesignV2RoutePath.CHECKOUT + CheckoutRoutePath.INFO;
  const reviewPathname = redesignV2RoutePath.CHECKOUT + CheckoutRoutePath.REVIEW;

  const orderSummaryRoutes = [infoPathname, reviewPathname];
  const shouldRenderOrderSummary = !isLoading && orderSummaryRoutes.includes(location.pathname);

  if (!userInfo && location.pathname !== infoPathname) {
    return <Redirect to={infoPathname} />;
  }

  const containerClasses = cx(
    styles.pageContainer,
    {
      [styles.withOrderSummary]: shouldRenderOrderSummary,
      [styles.pageContainerWithOrderSummary]: shouldRenderOrderSummary,
    }
  );

  return (
    <main className={containerClasses}>
      <header className={styles.header}>
        <Grid container justifyContent="center">
          <Grid item xs={12} lg={6} xl={5}>
            <ResponsiveProgressBar
              steps={PROGRESS_BAR_STEPS}
              currentStep={
                shouldRenderOrderSummary
                  ? PROGRESS_BAR_STEP_ENUM.FIRST_STEP
                  : PROGRESS_BAR_STEP_ENUM.SECOND_STEP
              }
            />
          </Grid>
        </Grid>
      </header>
      <GridContainer gutter justifyContent={shouldRenderOrderSummary ? 'flex-start' : 'center'}>
        {isLargeDesktop && shouldRenderOrderSummary && <Grid item xl={1} data-test="padding-column" />}
        <Grid
          item
          xs={12}
          md={shouldRenderOrderSummary ? 8 : 9}
          lg={shouldRenderOrderSummary ? 8 : 6}
          xl={6}
          className={styles.mainContent}
        >
          {isLoading ? <CheckoutLoading /> : children}
        </Grid>
        {isLargeDesktop && shouldRenderOrderSummary && <Grid item xl={1} />}
        {shouldRenderOrderSummary && (
          <Grid item xs={12} md={4} lg={4} xl={3} className={styles.orderSummary}>
            <aside data-test="order-summary-container">
              <OrderSummary
                isContinueButtonDisabled={!isUserInfoValid}
                continueButtonClickHandler={continueButtonClickHandler}
              />
            </aside>
          </Grid>
        )}
        {isLargeDesktop && shouldRenderOrderSummary && <Grid item xl={1} />}
      </GridContainer>
      <StructuredModal
        modalState={[hasApiError, setHasApiError]}
        title={t('something-went-wrong-title')}
        description={t('checkout-error.description')}
        actions={[
          {
            label: t('checkout-error.cta-label'),
            variant: LinkVariant.SECONDARY,
            isCloseButton: true,
          },
        ]}
      />
    </main>
  );
};

CheckoutBasePage.displayName = 'CheckoutBasePage';
